/**
 * constants.js
 *
 * Shared constants for the WorkflowPane component suite.
 * State machine, event names, and field type mappings for ActionDef → Formik forms.
 */

// ---- State Machine ----

export const WorkflowState = Object.freeze({
    EMPTY: 'EMPTY',
    PROPOSED: 'PROPOSED',
    EDITING: 'EDITING',
    VALIDATED: 'VALIDATED',
    SUBMITTED: 'SUBMITTED',
    COMPLETE: 'COMPLETE',
});

// ---- EventBroker Event Names ----

export const WorkflowEvents = Object.freeze({
    PROPOSED: 'workflowProposed',
    MODIFIED: 'workflowModified',
    SUBMITTED: 'workflowSubmitted',
    STEP_COMPLETE: 'workflowStepComplete',
    CANCELLED: 'workflowCancelled',
    COMPLETE: 'workflowComplete',
});

// ---- Validation Status per Step ----

export const StepStatus = Object.freeze({
    VALID: 'valid', // Green — preconditions met, params valid
    WARNING: 'warning', // Yellow — optional params missing or planned action
    INVALID: 'invalid', // Red — preconditions unmet, required params missing
    PENDING: 'pending', // Gray — not yet validated
});

// ---- Field Type Mapping ----
// Maps ActionDef ParamDef types to the Formik field component to render.

export const FieldType = Object.freeze({
    SELECT: 'select', // string with options → SelectWithFormikAndTooltip
    TEXT: 'text', // string without options → TextFieldWithInfoTooltip
    NUMBER: 'number', // number → TextFieldWithInfoTooltip type="number"
    INCREMENT: 'increment', // number with min/max → FormikIncrementDecrementInput
    CHECKBOX: 'checkbox', // boolean → MUI Checkbox
    READONLY: 'readonly', // complex types → read-only display
});

/**
 * Determine the FieldType for a given ParamDef.
 * @param {Object} paramDef - { name, type, options?, constraints?, required, defaultValue }
 * @returns {string} One of FieldType values.
 */
export function resolveFieldType(paramDef) {
    const { type, options, constraints } = paramDef;

    if (type === 'boolean') return FieldType.CHECKBOX;

    if (type === 'string' && Array.isArray(options) && options.length > 0) {
        return FieldType.SELECT;
    }

    if (type === 'number') {
        if (constraints && (constraints.min != null || constraints.max != null)) {
            return FieldType.INCREMENT;
        }
        return FieldType.NUMBER;
    }

    if (type === 'string') return FieldType.TEXT;

    // Complex / object / workflow types → read-only
    return FieldType.READONLY;
}

// ---- Panel Config ----

export const WORKFLOW_PAGE_ID = 'workflow';
export const WORKFLOW_PANEL_TITLE = 'Workflow Checker';
export const VALIDATION_DEBOUNCE_MS = 300;
